import React, { useEffect, useState } from "react";
import { Select } from "antd";
import { eResultCode } from "../../utils/enum";
import useFetch from "../../../src/hooks/useFetch";
import styles from "./customDropdown.module.css";

type TOptions = {
  value: any;
  label: string;
  isSelected?: boolean;
};

type TProps = {
  onChange: (option: TOptions | any) => void;
  endPoint: string;
  label?: string;
  placeHolder: string;
  variant?: string;
  addPaylod?: object;
  value?: any;
  error?: string;
  isRequired?: boolean;
  isDisabled?: boolean;
  optionId?: string;
  optionName?: string;
  fullPageForm?: boolean;
  fullPageFormArray?: boolean;
  new?: boolean;
  modal?: boolean;
  fullPageNew?: boolean;
  phoneView?: boolean;
  selectedName?: string;
};

const CustomDropdown = (props: TProps) => {
  const [dropdownOptions, setDropdownOptions] = useState<TOptions[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRows, setTotalRows] = useState(0);
  const { post } = useFetch();
  const [isLoading, setIsLoading] = useState(false);
  const isDisabled = props.isDisabled ?? false;
  const isFirstRender = React.useRef(true);

  // Fetch options based on the current page and search text
  const getOptions = async (searchText: string = "") => {
    if (props.endPoint) {
      setIsLoading(true);
      try {
        const payload = {
          data: {
            ...(props.addPaylod && { ...props.addPaylod }),
            pageSize: pageSize,
            currentPage: currentPage,
            searchText: searchText, // Pass searchText to API
          },
        };
        const response = await post(props.endPoint, payload);

        if (response.dataResponse.returnCode === eResultCode.SUCCESS) {
          const newOptions = response.data.map((item: any) => ({
            value: props.optionId ? item[props.optionId] : item?.id,
            label: props.optionName ? item[props.optionName] : item?.name,
            ...item,
          }));

          setTotalRows(response.filterModel?.filterRowsCount ?? 0);

          if (response.data?.length === 0) {
            setDropdownOptions([]); // Clear options if no data is found
          } else {
            // Add the new options to the existing list
            setDropdownOptions((prevOptions) => {
              const existingValues = prevOptions.map((opt) => opt.value);
              const filteredNewOptions = newOptions.filter(
                (newOpt: { value: any }) =>
                  !existingValues.includes(newOpt.value)
              );
              return [...prevOptions, ...filteredNewOptions];
            });
          }
        }
      } catch (error) {
        console.error("Error:", error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    if (
      props.addPaylod &&
      Object.values(props.addPaylod).some((value) => value !== null)
    ) {
      setDropdownOptions([]);
      setCurrentPage(1);
      setPageSize(10);
      setTotalRows(0);
      getOptions("");
    }
  }, [props.addPaylod]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (props.endPoint) {
      setDropdownOptions([]);
      setCurrentPage(1);
      setPageSize(10);
      setTotalRows(0);
      getOptions("");
    }
  }, [props.endPoint]);

  useEffect(() => {
    getOptions("");
  }, [currentPage, pageSize]);

  // Handle infinite scroll for loading more options
  const handlePopupScroll = (event: React.UIEvent<HTMLDivElement, UIEvent>) => {
    const target = event.target as HTMLDivElement;
    if (target.scrollTop + target.clientHeight >= target.scrollHeight) {
      if (totalRows > pageSize && totalRows > currentPage * pageSize) {
        setCurrentPage(currentPage + 1);
      }
    }
  };

  // Handle search input change and call API with search text
  const handleSearch = debounce((input: string) => {
    setDropdownOptions([]);
    setCurrentPage(1);
    getOptions(input);
  }, 300);

  // Debounce function to limit the rate of function execution
  function debounce(func: (...args: any[]) => void, wait: number) {
    let timeout: NodeJS.Timeout;
    return (...args: any[]) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), wait);
    };
  }

  // Ensure that the selected option (if any) is part of the dropdown options
  useEffect(() => {
    if (props.value && props.selectedName) {
      const optionExists = dropdownOptions.some(
        (opt) => opt.value === props.value
      );

      if (!optionExists) {
        // Add the selected option manually if it's not already in the options list
        setDropdownOptions(
          (prevOptions) =>
            [
              ...prevOptions,
              { value: props.value, label: props.selectedName }, // Use selectedName directly
            ] as any
        );
      }
    }
  }, [props.value, props.selectedName]);

  console.log(props.addPaylod);

  return (
    <div className={styles.mainDivStyle}>
      {props.label && (
        <span className={styles.lables}>
          {props.isRequired && <span className={styles.labelColor}>* </span>}
          <span style={{ marginLeft: "4px", color: "#1f1f1f !important" }}>
            {props.label}
          </span>
        </span>
      )}

      <Select
        variant={
          (props?.variant as "outlined" | "borderless" | "filled") || "outlined"
        }
        filterOption={false}
        showSearch={true} // Enable search input box
        placeholder={props.placeHolder || "Select..."}
        options={dropdownOptions}
        onSearch={handleSearch} // Triggered when user types in the search box
        onPopupScroll={handlePopupScroll} // Handles infinite scroll
        loading={isLoading}
        disabled={isDisabled}
        value={props.value ? props.value : null} // Use the value prop
        onChange={(value) => {
          const selectedOption = dropdownOptions.find(
            (opt) => opt.value === value
          );
          props.onChange(selectedOption || null);
        }}
        style={{ width: "100%" }}
      />

      {props.error && <span className={styles.errorLabel}>{props.error}</span>}
    </div>
  );
};

export default CustomDropdown;
